import { useEditor, useNode } from "@craftjs/core";
import { rem } from "polished";
import * as React from "react";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import Image, {
  ImageComponentProps,
} from "@/components/editor/primitives/legacy/image/image-component";
import Text, {
  TextComponentProps,
} from "@/components/editor/primitives/text/text-component";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
  BlockPaddingWrapper,
} from "@/components/editor/shared/block-wrapper";
import Element from "@/components/element/element-component";
import { IMAGE_UPLOAD_TYPES } from "@/features/analytics";

import ParagraphItem, { ParagraphItemProps } from "./paragraph-item-component";
import ParagraphPictureList from "./paragraph-picture-list-component";

export type ParagraphPictureItem =
  | ImageComponentProps
  | TextComponentProps
  | ParagraphItemProps;
export type ParagraphPictureProps = {
  paragraphPictures: ParagraphPictureItem[];
};

const PARAGRAPH_PICTURE_LIST_ID = "paragraph-picture-list";

function instanceOfParagraphItem(object: any): object is ParagraphItemProps {
  return "subTitle" in object && "description" in object;
}

function instanceOfText(object: any): object is ParagraphPictureProps {
  return "text" in object;
}

function instanceOfImage(object: any): object is ImageComponentProps {
  return "imageUrl" in object;
}

const imageStyle = {
  maxWidth: rem(456),
  maxHeight: rem(456),
  overflow: "hidden",
};

export default function ParagraphPicture(props: ParagraphPictureProps) {
  const {
    connectors: { connect },
    nodeId,
  } = useNode((node) => {
    return {
      nodeId: node.id,
      props: node.data.props,
    };
  });

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  return (
    <BlockBackgroundColorWrapper
      ref={connect}
      id={nodeId}
      data-testid="paragraph-picture"
    >
      <BlockPaddingWrapper>
        <BlockMaxWidthWrapper>
          <Element
            id={PARAGRAPH_PICTURE_LIST_ID}
            is={ParagraphPictureList}
            canvas={enabled}
          >
            {props.paragraphPictures?.map((paragraphPicture, index) => {
              if (instanceOfText(paragraphPicture)) {
                return (
                  <Text
                    key={index}
                    pattern="paragraph"
                    {...(paragraphPicture as TextComponentProps)}
                  />
                );
              } else if (instanceOfImage(paragraphPicture)) {
                return (
                  <Image
                    key={index}
                    style={imageStyle}
                    blockType={IMAGE_UPLOAD_TYPES.PARAGRAPH_AND_PICTURE}
                    objectPosition="center center"
                    {...(paragraphPicture as ImageComponentProps)}
                  />
                );
              } else if (instanceOfParagraphItem(paragraphPicture)) {
                return (
                  <ParagraphItem
                    key={index}
                    {...(paragraphPicture as ParagraphItemProps)}
                  />
                );
              }
            })}
          </Element>
        </BlockMaxWidthWrapper>
      </BlockPaddingWrapper>
    </BlockBackgroundColorWrapper>
  );
}

ParagraphPicture.craft = {
  custom: {
    displayName: "Paragraph & Picture",
  },
  related: {
    leftOutlineOptions: TopLevelFloatingOptions,
  },
  rules: {
    canMoveIn: () => false,
  },
};
